import { AutocompleteWithLabel, SelectWithLabel } from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Button,
  FormControlLabel,
  Grid,
  Grow,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  Typography
} from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'

import TimePeriodForm from '../../components/TimePeriodForm'
import { CHANNELS_QUERY } from '../../graphql/channel'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { USERS_LIST_QUERY } from '../../graphql/userManagement'
import { DateObject } from '../../helpers/date'
import {
  getEntityCode,
  getEntityId,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { Channel } from '../../types/channel'
import { AllocateAction, AllocationStatus } from '../../types/inventoryAllocate'
import { AllocationLogsInput } from '../../types/inventoryAllocationLogs'
import { RoomTypeBrief } from '../../types/roomtype'
import { UserList } from '../../types/users'
import useStyles from './styles/AllocationHistoryFilters.styles'

interface AllocationHistoryFiltersProps {
  allocationLogsInput: AllocationLogsInput
  perPage: number
  setallocationLogsInput: React.Dispatch<
    React.SetStateAction<AllocationLogsInput>
  >
  onFindClick: () => void
  propertyCode: string
  loadingForLogs: boolean
  autoCompleteLabelRoomType: {
    label: string
    value: string
    code: string
  }
  setautoCompleteLabelRoomType: (v: {
    label: string
    value: string
    code: string
  }) => void
}

const AllocationHistoryFilters: React.FC<AllocationHistoryFiltersProps> = ({
  allocationLogsInput,
  setallocationLogsInput,
  onFindClick,
  propertyCode,
  loadingForLogs,
  perPage
}) => {
  const classes = useStyles()
  const [requestTimeSelected, setrequestTimeSelected] = useState<boolean>(false)
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const [roomtypeOptions, setroomtypeOptions] = useState<RoomTypeBrief[]>([])
  const [autoCompleteForRoomType, setAutoCompleteForRoomType] =
    useState<any>(null)
  const [autoCompleteForChannels, setAutoCompleteForChannels] =
    useState<any>(null)
  const [autoCompleteLabelForUser, setautoCompleteLabelForUser] =
    useState<any>(null)
  const [dateObjectRequestTime, setDateObjectRequestTime] =
    useState<DateObject>()
  const [tableType, settableType] = useState<'SystemChanges' | 'UserChanges'>(
    'UserChanges'
  )
  const datePickerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const isrequestTimeSelected = requestTimeSelected

      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node)
      ) {
        if (isrequestTimeSelected) {
          setrequestTimeSelected(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, requestTimeSelected])

  const [getRoomTypes, { loading: loadingForRoomTypes }] = useLazyQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setroomtypeOptions(data.roomTypes)
    }
  })

  const { data: channelsData, loading: loadingForChannels } = useQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY)

  const { data: usersData, loading: loadingForUsers } = useQuery<{
    users: UserList
  }>(USERS_LIST_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    if (propertyCode) {
      getRoomTypes({
        variables: {
          propertyCode: propertyCode,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: true
        }
      })
    } else if (getRoleFromStorage() === 'ACCOMMODATION') {
      getRoomTypes({
        variables: {
          propertyCode: getEntityCode(),
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: true
        }
      })
    } else {
      setroomtypeOptions([])
    }
  }, [propertyCode, getEntityId()])

  const actionOptions: AllocateAction[] = ['allocated', 'deallocated']
  const statusOptions: AllocationStatus[] = [
    'Failed',
    'InProgress',
    'Retrying',
    'Successfull'
  ]

  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.selectBoxes}>
          <div className={classes.boxContainerRow}>
            <AutocompleteWithLabel
              label={getTranslate('User Email')}
              value={autoCompleteLabelForUser}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              options={
                usersData
                  ? usersData?.users.users.map((userItem) => ({
                      label: userItem.email,
                      value: userItem.email,
                      code: userItem.email,
                      id: userItem.ID
                    }))
                  : []
              }
              onChange={(_, v: any) => {
                if (v) {
                  setautoCompleteLabelForUser(v)
                  setallocationLogsInput({
                    ...allocationLogsInput,
                    userEmail: v.value
                  })
                } else {
                  setautoCompleteLabelForUser(null)
                  setallocationLogsInput({
                    ...allocationLogsInput,
                    userEmail: undefined
                  })
                }
              }}
              fullWidth
              noHelperText
              inputSize="small"
              size="small"
              loading={loadingForUsers}
              loadingText={getTranslate('Loading') + '...'}
            />
            <AutocompleteWithLabel
              value={autoCompleteForRoomType}
              label={getTranslate('Room Type')}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              options={roomtypeOptions.map((item) => ({
                label: item.name,
                value: item.displayRoomTypeCode || '',
                code: item.displayRoomTypeCode || '',
                id: item.id
              }))}
              onChange={(_, v: any) => {
                if (v) {
                  setAutoCompleteForRoomType(v)
                  setallocationLogsInput({
                    ...allocationLogsInput,
                    roomTypeCode: v.code
                  })
                } else {
                  setAutoCompleteForRoomType(null)
                  setallocationLogsInput({
                    ...allocationLogsInput,
                    roomTypeCode: undefined
                  })
                }
              }}
              fullWidth
              noHelperText
              inputSize="small"
              size="small"
              loading={loadingForRoomTypes}
              loadingText={getTranslate('Loading') + '...'}
            />
            <AutocompleteWithLabel
              noHelperText
              fullWidth
              value={autoCompleteForChannels}
              label={getTranslate('Channel')}
              inputSize="small"
              size="small"
              options={
                channelsData
                  ? channelsData?.channels?.map((item) => ({
                      label: item.basicDetails.name,
                      value: item.basicDetails.displayChannelCode || '',
                      code: item.basicDetails.displayChannelCode,
                      id: item.basicDetails.channelID || ''
                    }))
                  : []
              }
              onChange={(_, v: any) => {
                if (v) {
                  setAutoCompleteForChannels(v)
                  setallocationLogsInput({
                    ...allocationLogsInput,
                    channelCode: v.value
                  })
                } else {
                  setAutoCompleteForChannels(null)
                  setallocationLogsInput({
                    ...allocationLogsInput,
                    channelCode: undefined
                  })
                }
              }}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              loading={loadingForChannels}
              loadingText={getTranslate('Loading') + '...'}
            />
            <div ref={datePickerRef}>
              <Typography>{getTranslate('Request Time')}</Typography>
              <TimePeriodForm
                isOpen={requestTimeSelected}
                setTimePickerSelected={setrequestTimeSelected}
                dateObject={dateObjectRequestTime}
                onChange={(data: DateObject) => {
                  setDateObjectRequestTime(data)
                  setallocationLogsInput({
                    ...allocationLogsInput,
                    requestTimeRange: {
                      from: data.gregorianStartDate || '',
                      to: data.gregorianStartDate || ''
                    }
                  })
                }}
                onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
                setOnDoneClickForCloseDatePicker={
                  setOnDoneClickForCloseDatePicker
                }
                enableAllDays
              />
            </div>
          </div>
          <div className={classes.boxContainerRow}>
            <SelectWithLabel
              fullWidth
              value={allocationLogsInput.action || ''}
              onChange={(event) =>
                setallocationLogsInput({
                  ...allocationLogsInput,
                  action: event.target.value as AllocateAction
                })
              }
              size="small"
              label={getTranslate('Action')}>
              {actionOptions.map((action) => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <SelectWithLabel
              fullWidth
              value={allocationLogsInput.status || ''}
              onChange={(event) =>
                setallocationLogsInput({
                  ...allocationLogsInput,
                  status: event.target.value as AllocationStatus
                })
              }
              size="small"
              label={getTranslate('Status')}>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <div className={classes.inventoryTypeSelector}>
              <Typography className={classes.inventoryTypeSelectorLabel}>
                {getTranslate('Table')}
              </Typography>
              <RadioGroup
                className={classes.inventoryTypeRadioGroup}
                value={tableType}
                onChange={(event) => {
                  settableType(
                    event.target.value as 'SystemChanges' | 'UserChanges'
                  )
                  setallocationLogsInput({
                    ...allocationLogsInput,
                    showSystemChanges:
                      (event.target.value as
                        | 'SystemChanges'
                        | 'UserChanges') === 'SystemChanges'
                  })
                }}>
                <FormControlLabel
                  value="UserChanges"
                  control={<Radio color="primary" />}
                  label={getTranslate('User Changes')}
                />
                <FormControlLabel
                  value="SystemChanges"
                  control={<Radio color="primary" />}
                  label={getTranslate('System Changes')}
                />
              </RadioGroup>
            </div>

            <div className={classes.switchContainer}>
              <Typography component="div" className={classes.switch}>
                <Grid
                  component="label"
                  container
                  alignItems="center"
                  justify="flex-start">
                  <Grid item>
                    <Switch
                      name="active"
                      color="primary"
                      onChange={() => {
                        setallocationLogsInput({
                          ...allocationLogsInput,
                          canRetry: !allocationLogsInput.canRetry
                        })
                      }}
                      checked={allocationLogsInput.canRetry}
                    />
                  </Grid>
                </Grid>
              </Typography>
              <Typography variant="body1">
                {getTranslate('Can Retry')}
              </Typography>
            </div>
          </div>
        </div>
        <div className={classes.ButtonsContainer}>
          <Button
            className={classes.resetButton}
            variant="text"
            color="secondary"
            onClick={() => {
              setautoCompleteLabelForUser(null)
              setAutoCompleteForRoomType(null)
              setAutoCompleteForChannels(null)
              setDateObjectRequestTime({})
              settableType('UserChanges')
              setallocationLogsInput({
                propertyCode: propertyCode,
                showSystemChanges: false,
                page: 1,
                pageSize: perPage
              })
            }}>
            {getTranslate('Reset')}
          </Button>

          <Button
            className={classes.applyButton}
            variant="contained"
            color="primary"
            disabled={loadingForLogs}
            onClick={() => {
              onFindClick()
            }}>
            {getTranslate('Apply')}
          </Button>
        </div>
      </div>
    </Grow>
  )
}

export default AllocationHistoryFilters
